/**
 * Copyright 2024 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { createContext, FC, ReactNode, useContext, useState, useCallback, useEffect } from "react";
import { ElevenLabsService, ElevenLabsVoice, TextToSpeechOptions, VoiceCloningOptions, getElevenLabsService } from "../lib/elevenlabs";

export interface ElevenLabsContextType {
  service: ElevenLabsService | null;
  isLoading: boolean;
  error: string | null;
  voices: ElevenLabsVoice[];
  currentVoice: ElevenLabsVoice | null;
  isSpeaking: boolean;
  
  // Actions
  initializeService: (apiKey: string) => void;
  loadVoices: () => Promise<void>;
  speakText: (options: TextToSpeechOptions) => Promise<void>;
  stopSpeaking: () => void;
  cloneVoice: (options: VoiceCloningOptions) => Promise<string>;
  setVoice: (voice: ElevenLabsVoice) => void;
  clearError: () => void;
}

const ElevenLabsContext = createContext<ElevenLabsContextType | undefined>(undefined);

export type ElevenLabsProviderProps = {
  children: ReactNode;
  apiKey?: string;
};

export const ElevenLabsProvider: FC<ElevenLabsProviderProps> = ({
  children,
  apiKey: initialApiKey,
}) => {
  const [service, setService] = useState<ElevenLabsService | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [voices, setVoices] = useState<ElevenLabsVoice[]>([]);
  const [currentVoice, setCurrentVoice] = useState<ElevenLabsVoice | null>(null);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [currentAudio, setCurrentAudio] = useState<HTMLAudioElement | null>(null);

  // Initialize service with API key
  const initializeService = useCallback((apiKey: string) => {
    try {
      const newService = getElevenLabsService(apiKey);
      setService(newService);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to initialize Eleven Labs service');
    }
  }, []);

  // Load available voices
  const loadVoices = useCallback(async () => {
    if (!service) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      const availableVoices = await service.getVoices();
      setVoices(availableVoices);
      
      // Set default voice if none selected
      if (!currentVoice && availableVoices.length > 0) {
        const defaultVoice = availableVoices.find(voice => voice.name === 'Rachel') || availableVoices[0];
        setCurrentVoice(defaultVoice);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load voices');
    } finally {
      setIsLoading(false);
    }
  }, [service, currentVoice]);

  // Speak text using text-to-speech
  const speakText = useCallback(async (options: TextToSpeechOptions) => {
    if (!service) {
      setError('Eleven Labs service not initialized');
      return;
    }

    setIsSpeaking(true);
    setError(null);

    try {
      // Use current voice if none specified
      const voiceId = options.voice_id || currentVoice?.voice_id || 'rachel';
      
      // Get audio buffer
      const audioBuffer = await service.textToSpeech({
        ...options,
        voice_id: voiceId,
      });

      // Create and play audio
      const audioBlob = new Blob([audioBuffer], { type: 'audio/mpeg' });
      const audioUrl = URL.createObjectURL(audioBlob);
      const audio = new Audio(audioUrl);
      
      audio.onended = () => {
        setIsSpeaking(false);
        URL.revokeObjectURL(audioUrl);
        setCurrentAudio(null);
      };

      audio.onerror = () => {
        setIsSpeaking(false);
        setError('Audio playback failed');
        URL.revokeObjectURL(audioUrl);
        setCurrentAudio(null);
      };

      setCurrentAudio(audio);
      await audio.play();
    } catch (err) {
      setIsSpeaking(false);
      setError(err instanceof Error ? err.message : 'Failed to speak text');
    }
  }, [service, currentVoice]);

  // Stop speaking
  const stopSpeaking = useCallback(() => {
    if (currentAudio) {
      currentAudio.pause();
      currentAudio.currentTime = 0;
      setCurrentAudio(null);
    }
    setIsSpeaking(false);
  }, [currentAudio]);

  // Clone a voice
  const cloneVoice = useCallback(async (options: VoiceCloningOptions): Promise<string> => {
    if (!service) {
      setError('Eleven Labs service not initialized');
      throw new Error('Service not initialized');
    }

    setIsLoading(true);
    setError(null);

    try {
      const voiceId = await service.cloneVoice(options);
      await loadVoices(); // Reload voices to include the new one
      return voiceId;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to clone voice';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [service, loadVoices]);

  // Set current voice
  const setVoice = useCallback((voice: ElevenLabsVoice) => {
    setCurrentVoice(voice);
  }, []);

  // Clear error
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Initialize service on mount if API key provided
  useEffect(() => {
    if (initialApiKey) {
      initializeService(initialApiKey);
    }
  }, [initialApiKey, initializeService]);

  // Load voices when service is initialized
  useEffect(() => {
    if (service) {
      loadVoices();
    }
  }, [service, loadVoices]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopSpeaking();
    };
  }, [stopSpeaking]);

  const value: ElevenLabsContextType = {
    service,
    isLoading,
    error,
    voices,
    currentVoice,
    isSpeaking,
    initializeService,
    loadVoices,
    speakText,
    stopSpeaking,
    cloneVoice,
    setVoice,
    clearError,
  };

  return (
    <ElevenLabsContext.Provider value={value}>
      {children}
    </ElevenLabsContext.Provider>
  );
};

export const useElevenLabsContext = () => {
  const context = useContext(ElevenLabsContext);
  if (!context) {
    throw new Error("useElevenLabsContext must be used within an ElevenLabsProvider");
  }
  return context;
};
